import React, { useState } from "react";
import { Plus, Minus, MessageCircleQuestion } from "lucide-react";

const FAQS = [
  {
    q: "Quanto tempo demora a montagem dos pneus?",
    a: "Para um carro ligeiro, a montagem de 4 pneus (com equilibragem e alinhamento) demora em média 60 minutos. Se trouxer marcação, atendemos no horário combinado sem espera. Em dias de maior afluência (sábados de manhã geralmente), recomendamos marcação prévia.",
  },
  {
    q: "Têm pneus em stock ou é por encomenda?",
    a: "Mantemos stock permanente das medidas mais comuns (turismo e SUV) e das marcas com que trabalhamos diretamente (Hankook, Michelin, Continental, Taurus, Sailun, entre outras). Para medidas especiais ou modelos premium específicos, fazemos encomenda — entrega habitualmente em 24 a 48h úteis.",
  },
  {
    q: "Fazem reparação de jantes?",
    a: "Não fazemos reparação de jantes diretamente, mas trabalhamos com parceiros especializados de confiança. Se trouxer o veículo, fazemos o diagnóstico e encaminhamos para o parceiro mais indicado conforme o tipo de dano (estética, empeno, fissura).",
  },
  {
    q: "Quando se deve fazer a rotação dos pneus?",
    a: "Após a montagem de pneus novos, deve trocá-los de posição com os pneus da frente (sempre que tal seja possível) depois de fazer entre 10.000 a 15.000 km de estrada. A rotação prolonga a vida útil do conjunto e mantém o desgaste uniforme.",
  },
  {
    q: "Em que eixo se devem montar os pneus novos?",
    a: "A recomendação unânime da indústria — Michelin, Continental, Bridgestone e Pirelli — é montar os pneus novos (ou com maior profundidade de sulco) no eixo traseiro, independentemente da tração do veículo. Em piso molhado ou em curva, pneus gastos atrás podem perder aderência primeiro e provocar sobreviragem (a traseira do carro 'foge'), uma situação muito mais difícil de controlar do que subviragem. Por segurança, ao trocar apenas 2 pneus colocamos sempre os novos no traseiro.",
  },
  {
    q: "Quanto custa um alinhamento?",
    a: "O alinhamento de direção custa a partir de 20€. Aconselhamos sempre verificar quando trocar pneus, em caso de embate em buraco ou se notar desgaste irregular. Em conjunto com troca de pneus, fazemos preço promocional.",
  },
  {
    q: "Que formas de pagamento aceitam?",
    a: "Aceitamos dinheiro, multibanco, MB Way e transferência bancária. Para empresas, emitimos fatura com NIF para dedução. Não trabalhamos com pagamento em prestações no momento.",
  },
  {
    q: "Reparam pneus furados?",
    a: "Sim, fazemos reparação de furos sempre que possível, dentro das normas de segurança (apenas furos na banda de rolamento e até determinado diâmetro). Se a reparação não for viável em segurança, recomendamos a substituição. Para emergências de furo, ligue diretamente — atendemos com prioridade.",
  },
  {
    q: "Têm garantia nos serviços e pneus?",
    a: "Todos os pneus novos vendidos têm garantia de fabricante. A montagem e calibragem que fazemos têm garantia da casa. Se notar algum problema após o serviço (vibração, perda de pressão, desgaste irregular), volte para verificarmos sem custo.",
  },
];

export const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);

  const toggle = (idx) => {
    setOpenIdx((current) => (current === idx ? -1 : idx));
  };

  return (
    <section
      data-testid="faq-section"
      id="faq"
      className="relative pt-28 pb-14 md:pt-32 md:pb-16 bg-[#0E2452] border-t border-[#2F568F]"
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
            <div>
              <span className="section-label flex items-center gap-2">
                <MessageCircleQuestion className="w-4 h-4" />
                // Resposta Instantânea
              </span>
              <h2 className="font-display font-bold text-white text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight mt-3 leading-[0.98]">
                Perguntas
                <br />
                <span className="text-[#FF5A00]">frequentes</span>
              </h2>
            </div>
            <p className="text-[#A1A1AA] font-body text-sm md:text-base max-w-md">
              Tire as suas dúvidas em segundos. Se não encontrar resposta,
              <a
                href="#orcamento-cta"
                className="text-[#FF5A00] hover:text-white transition-colors ml-1"
              >
                contacte-nos
              </a>
              .
            </p>
          </div>

          {/* Accordion */}
          <div className="glass-soft divide-y divide-white/10 rounded-2xl">
            {FAQS.map((item, idx) => {
              const isOpen = openIdx === idx;
              return (
                <div key={item.q} data-testid={`faq-item-${idx}`} className="px-4 md:px-6">
                  <button
                    type="button"
                    onClick={() => toggle(idx)}
                    data-testid={`faq-toggle-${idx}`}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-5 py-4 md:py-5 text-left group active:scale-[0.99] transition-transform"
                  >
                    <span
                      className={`font-display font-semibold uppercase tracking-tight text-sm md:text-lg transition-colors ${
                        isOpen ? "text-[#FF5A00]" : "text-white group-hover:text-[#FF5A00]"
                      }`}
                    >
                      {item.q}
                    </span>
                    <span
                      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
                        isOpen
                          ? "bg-[#FF5A00] border-[#FF5A00] text-black rotate-180"
                          : "bg-transparent border-[#2F568F] text-white group-hover:border-[#FF5A00] group-hover:text-[#FF5A00]"
                      }`}
                    >
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      isOpen ? "max-h-96 opacity-100 pb-5" : "max-h-0 opacity-0"
                    }`}
                  >
                    <p
                      data-testid={`faq-answer-${idx}`}
                      className="text-[#A1A1AA] font-body text-sm md:text-base leading-relaxed pr-12 md:pr-16"
                    >
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div
            id="orcamento-cta"
            className="mt-8 rounded-2xl bg-[#163A6D] border border-[#2F568F] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <div className="font-display font-bold text-white uppercase text-lg md:text-xl">
                A sua dúvida não está aqui?
              </div>
              <p className="text-[#A1A1AA] font-body text-sm mt-1">
                Pedimos só dois minutos. Respondemos no mesmo dia útil.
              </p>
            </div>
            <a
              href="/orcamento"
              data-testid="faq-cta-orcamento"
              className="btn-primary text-sm py-3 px-6 whitespace-nowrap"
            >
              Pedir Orçamento
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
